import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const usePersonaAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [currentPersona, setCurrentPersona] = useState(null);

  // Fetch all personas (display list)
  const fetchPersonas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.personaDisplayList());
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching personas:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch persona by ID
  const fetchPersonaById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.personaById(id));
      setCurrentPersona(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching persona:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new persona
  const createPersona = useCallback(async (personaData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(
        apiUrl.createPersona(),
        personaData
      );
      setPersonas((prevPersonas) => [...prevPersonas, response.data]);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error creating persona:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update persona
  const updatePersona = useCallback(async (id, personaData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.put(
        apiUrl.updatePersona(id),
        personaData
      );
      setPersonas((prevPersonas) =>
        prevPersonas.map((p) => (p.id === id ? response.data : p))
      );
      if (currentPersona?.id === id) {
        setCurrentPersona(response.data);
      }
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error updating persona:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentPersona]);

  // Delete persona
  const deletePersona = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await apiService.delete(apiUrl.deletePersona(id));
      setPersonas((prevPersonas) =>
        prevPersonas.filter((p) => p.id !== id)
      );
      if (currentPersona?.id === id) {
        setCurrentPersona(null);
      }
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error deleting persona:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentPersona]);

  // Fetch mapped report IDs for a persona
  const fetchMappedReports = useCallback(async (personaId) => {
    try {
      const response = await apiService.get(apiUrl.personaMappedReportsById(personaId));
      const data = response.data;
      return data?.data ?? (Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching mapped reports:", err.response?.data?.message || err.message);
      return [];
    }
  }, []);

  // Save report mapping for a persona
  const saveReportMapping = useCallback(async (personaName, reportIds) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.personaMapReports(), { personaName, reportIds });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error saving report mapping:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch the list of active user groups (id/name pairs) for dropdowns
  const fetchPersonaUserGroups = useCallback(async () => {
    try {
      const response = await apiService.get(apiUrl.personaUserGroups());
      return response.data?.data || response.data || [];
    } catch (err) {
      console.error("Error fetching persona user groups:", err.response?.data?.message || err.message);
      throw err;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear current persona
  const clearCurrentPersona = useCallback(() => {
    setCurrentPersona(null);
  }, []);

  return {
    loading,
    error,
    personas,
    currentPersona,
    fetchPersonas,
    fetchPersonaById,
    createPersona,
    updatePersona,
    deletePersona,
    fetchMappedReports,
    saveReportMapping,
    fetchPersonaUserGroups,
    clearError,
    clearCurrentPersona,
  };
};

export default usePersonaAdmin;
